import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ServiceTypeTags from 'components/CreateBooking/Daycare/UI/ServiceTypeTags';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { BusinessAreaType, Service, SERVICES_BUSINESS_AREAS } from 'model/Service';
import { RootState } from 'store';
import styles from './FormServiceSelector.module.scss';

type ServiceType =
    | 'memberships'
    | 'grooming'
    | 'dental'
    | 'daycare'
    | 'boarding'
    | 'transportation'
    | 'walking'
    | 'training'
    | 'evaluation'
    | undefined;

export type FormServiceCategory = Extract<
    BusinessAreaType,
    'grooming' | 'daycare' | 'boarding' | 'transportation' | 'walking' | 'training'
>;

const FETCH_AREA: Record<FormServiceCategory, string> = {
    grooming: `${SERVICES_BUSINESS_AREAS.GROOMING},${SERVICES_BUSINESS_AREAS.DENTAL}`,
    daycare: SERVICES_BUSINESS_AREAS.DAYCARE,
    boarding: SERVICES_BUSINESS_AREAS.BOARDING,
    transportation: SERVICES_BUSINESS_AREAS.TRANSPORTATION,
    walking: SERVICES_BUSINESS_AREAS.WALKING,
    training: SERVICES_BUSINESS_AREAS.TRAINING
};

interface Props {
    selectedIds: number[];
    lockedIds?: number[];
    initialCategory?: FormServiceCategory;
    associatedServices?: Array<{ id: number; businessArea?: string }>;
    disabled?: boolean;
    onChange: (serviceIds: number[]) => void;
    onCategoryChange?: (category: FormServiceCategory) => void;
}

const toFormCategory = (serviceType: ServiceType | string | undefined): FormServiceCategory | null => {
    if (!serviceType || serviceType === 'memberships' || serviceType === 'evaluation') {
        return null;
    }
    if (serviceType === 'dental') {
        return SERVICES_BUSINESS_AREAS.GROOMING;
    }
    if (
        serviceType === 'grooming' ||
        serviceType === 'daycare' ||
        serviceType === 'boarding' ||
        serviceType === 'transportation' ||
        serviceType === 'walking' ||
        serviceType === 'training'
    ) {
        return serviceType;
    }
    return null;
};

const resolveCategory = (
    initialCategory: FormServiceCategory | undefined,
    associatedServices: Array<{ id: number; businessArea?: string }>,
    selectedIds: number[],
    servicesByBusinessArea?: RootState['services']['servicesByBusinessArea']
): FormServiceCategory => {
    const selected = new Set(selectedIds.map(Number));
    if (selected.size > 0) {
        const matched = associatedServices.find(service => selected.has(Number(service.id)));
        const fromSelected = toFormCategory(matched?.businessArea);
        if (fromSelected) return fromSelected;
    }

    const fromServices = toFormCategory(associatedServices[0]?.businessArea);
    if (fromServices) return fromServices;

    if (servicesByBusinessArea && selected.size > 0) {
        const areas: Array<FormServiceCategory | 'dental'> = [
            'grooming',
            'dental',
            'daycare',
            'boarding',
            'transportation',
            'walking',
            'training'
        ];
        for (const area of areas) {
            const list = servicesByBusinessArea[area]?.services ?? [];
            if (list.some(service => selected.has(Number(service.id)))) {
                const category = toFormCategory(area);
                if (category) return category;
            }
        }
    }

    return initialCategory || SERVICES_BUSINESS_AREAS.GROOMING;
};

export const FormServiceSelector: React.FC<Props> = ({
    selectedIds,
    lockedIds = [],
    initialCategory,
    associatedServices = [],
    disabled,
    onChange,
    onCategoryChange
}) => {
    const dispatch = useDispatch();
    const servicesByBusinessArea = useSelector((state: RootState) => state.services.servicesByBusinessArea);
    const lockedSet = useMemo(
        () => new Set(lockedIds.map(Number).filter(id => Number.isFinite(id) && id > 0)),
        [lockedIds]
    );
    const [activeCategory, setActiveCategory] = useState<FormServiceCategory>(() =>
        resolveCategory(initialCategory, associatedServices, selectedIds, servicesByBusinessArea)
    );
    const hasUserChangedCategory = useRef(false);

    const keepLocked = (ids: number[]) => Array.from(new Set([...ids, ...Array.from(lockedSet)]));

    const handleSelectServiceType = (serviceType: ServiceType) => {
        if (disabled) return;
        const category = toFormCategory(serviceType);
        if (!category || category === activeCategory) return;
        hasUserChangedCategory.current = true;
        setActiveCategory(category);
        onCategoryChange?.(category);
    };

    const associatedCategoryKey = associatedServices
        .map(service => `${service.id}:${service.businessArea || ''}`)
        .join('|');
    const selectedIdsKey = selectedIds.map(Number).join(',');

    useEffect(() => {
        if (hasUserChangedCategory.current) return;
        const nextCategory = resolveCategory(initialCategory, associatedServices, selectedIds, servicesByBusinessArea);
        setActiveCategory(prev => (prev === nextCategory ? prev : nextCategory));
        onCategoryChange?.(nextCategory);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCategory, associatedCategoryKey, selectedIdsKey]);

    useEffect(() => {
        dispatch(fetchServicesThunk(undefined, 1, 200, 'active', 'asc', '', FETCH_AREA[activeCategory]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCategory]);

    const visibleServices = useMemo(() => {
        const areaServices = servicesByBusinessArea[activeCategory]?.services ?? [];
        const dentalServices =
            activeCategory === SERVICES_BUSINESS_AREAS.GROOMING
                ? servicesByBusinessArea[SERVICES_BUSINESS_AREAS.DENTAL]?.services ?? []
                : [];
        return [...areaServices, ...dentalServices].filter((service: Service) => service.id != null);
    }, [activeCategory, servicesByBusinessArea]);

    const visibleIds = visibleServices.map(service => Number(service.id));
    const selectedSet = useMemo(() => new Set(selectedIds.map(Number)), [selectedIds]);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedSet.has(id));

    const toggleAllVisible = (checked: boolean) => {
        if (disabled) return;
        if (checked) {
            onChange(keepLocked([...selectedIds.map(Number), ...visibleIds]));
            return;
        }
        const visible = new Set(visibleIds);
        onChange(keepLocked(selectedIds.map(Number).filter(id => !visible.has(id) || lockedSet.has(id))));
    };

    const toggleService = (serviceId: number, checked: boolean) => {
        if (disabled || lockedSet.has(serviceId)) return;
        if (checked) {
            onChange(keepLocked([...selectedIds.map(Number), serviceId]));
            return;
        }
        onChange(keepLocked(selectedIds.map(Number).filter(id => id !== serviceId)));
    };

    return (
        <div className={styles.section}>
            <h3 className={styles.title}>Select Services</h3>
            <div className={`${styles.categories} ${disabled ? styles.disabled : ''}`}>
                <ServiceTypeTags
                    selectedServiceType={activeCategory}
                    onSelectServiceType={handleSelectServiceType}
                    showMembershipsTag={false}
                    disableMembershipsAutoSelect
                    hideLabel
                />
            </div>
            <div className={`${styles.list} ${disabled ? styles.disabled : ''}`}>
                <div className={`${styles.row} ${styles.allServices}`}>
                    <Checkbox
                        label="All Services"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisible}
                    />
                </div>
                {visibleServices.map(service => (
                    <div
                        key={service.id}
                        className={styles.row}
                    >
                        <div
                            onClickCapture={event => {
                                if (lockedSet.has(Number(service.id))) {
                                    event.stopPropagation();
                                    event.preventDefault();
                                }
                            }}
                        >
                            <Checkbox
                                key={`${service.id}-${selectedSet.has(Number(service.id))}`}
                                label={service.name}
                                checked={selectedSet.has(Number(service.id))}
                                onChange={checked => toggleService(Number(service.id), checked)}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FormServiceSelector;
